import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { APIError } from '../services/api';
import { fetchEvents, filterEvents, filterEventsByCategory, fallbackCategories, fetchCategoryNames } from '../services/eventsService';
import EventCard from '../components/ui/EventCard';
import { EventGridSkeleton } from '../components/ui/EventCardSkeleton';
import EmptyState from '../components/ui/EmptyState';

// Animation variants for staggered grid
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

function Home() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [searchParams] = useSearchParams();
  // The submitted query (from the TopBar navigating to /?q=...). Drives the
  // server-side fetch. `searchQuery` below is the live input value and only
  // filters locally as the user types.
  const serverQuery = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(serverQuery);
  const [activeCategory, setActiveCategory] = useState('All');
  // Pills come from GET /api/categories; a hardcoded list drifted out of sync
  // with the real taxonomy and made most filters match nothing.
  const [availableCategories, setAvailableCategories] = useState(fallbackCategories);
  const [filters, setFilters] = useState({
    category: '',
    location: '',
  });

  const MAX_RETRY_ATTEMPTS = 3;

  const loadEvents = useCallback(async (showLoadingSpinner = true, isRetry = false) => {
    try {
      if (showLoadingSpinner) {
        setLoading(true);
      }
      setError(null);

      // Forward the submitted search term to the backend. GET /api/events
      // already supports `q` (title/description/location/city), so a submitted
      // search now covers the whole catalogue rather than only the events this
      // page happens to have loaded. We key off the URL param, not the input
      // state, so typing filters locally and only Enter/submit hits the server.
      const eventData = await fetchEvents(serverQuery ? { ...filters, q: serverQuery } : filters);
      const filteredEventData = filterEvents(eventData, filters);

      setEvents(filteredEventData);
      setRetryCount(0);
    } catch (err) {
      let errorMessage = 'Failed to load events';
      let shouldRetry = false;

      if (err instanceof APIError) {
        errorMessage = err.message;
        shouldRetry = ['NETWORK_ERROR', 'TIMEOUT_ERROR', 'SERVER_ERROR', 'SERVICE_UNAVAILABLE'].includes(err.code);

        if (err.code === 'UNAUTHORIZED') {
          errorMessage = 'Please log in to view events';
        } else if (err.code === 'NOT_FOUND') {
          errorMessage = 'No events found';
          setEvents([]);
          return;
        }
      } else if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
        shouldRetry = true;
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (shouldRetry && isRetry && retryCount < MAX_RETRY_ATTEMPTS) {
        setRetryCount((prev) => prev + 1);
        const delay = 1000 * Math.pow(2, retryCount);
        setTimeout(() => {
          loadEvents(false, true);
        }, delay);
        return;
      }

      setError(errorMessage);
      setEvents([]);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  }, [filters, retryCount, serverQuery]);

  useEffect(() => {
    const controller = new AbortController();

    const loadEventsWithCleanup = async () => {
      try {
        await loadEvents(true, false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Swallow non-abort errors -- they are already captured in state.
        }
      }
    };

    loadEventsWithCleanup();

    return () => {
      controller.abort();
    };
  }, [filters, loadEvents]);

  // Keep the search box in sync with the ?q= URL param (e.g. when the top-bar
  // search navigates here from another page).
  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
  }, [searchParams]);

  // Load the real category taxonomy for the filter pills.
  useEffect(() => {
    let ignore = false;
    fetchCategoryNames().then((names) => {
      if (!ignore) setAvailableCategories(names);
    });
    return () => {
      ignore = true;
    };
  }, []);

  const handleRetry = () => {
    setRetryCount(0);
    loadEvents(true, true);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    setFilters((prev) => ({
      ...prev,
      category: category === 'All' ? '' : category,
    }));
  };

  // Derive displayed events from category filter on the client side as well
  const displayedEvents = activeCategory === 'All'
    ? events
    : filterEventsByCategory(events, activeCategory);

  // Further filter by search query on title / city
  const searchFilteredEvents = searchQuery.trim()
    ? displayedEvents.filter((event) => {
        const q = searchQuery.toLowerCase();
        const title = (event.Title || event.title || '').toLowerCase();
        const city = (event.City || event.city || '').toLowerCase();
        const state = (event.State || event.state || '').toLowerCase();
        const description = (event.Description || event.description || '').toLowerCase();
        return title.includes(q) || city.includes(q) || state.includes(q) || description.includes(q);
      })
    : displayedEvents;

  return (
    <div>
      {/* ---------------------------------------------------------------- */}
      {/* Hero section                                                     */}
      {/* ---------------------------------------------------------------- */}
      <section className="mb-8">
        <h1 className="text-display text-surface-900 mb-2">
          Discover Events Near You
        </h1>
        <p className="text-body-lg text-surface-500 max-w-2xl mb-6">
          Find your next adventure. Join events that match your interests.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search bar */}
          <div className="relative flex-1">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events by name or city..."
              className="w-full bg-white border border-surface-200 rounded-xl pl-11 pr-4 py-3 text-body text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
              aria-label="Search events"
            />
          </div>

          {/* Create Event CTA */}
          <Link
            to="/create-cliqq"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200 shadow-colored flex-shrink-0"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Event
          </Link>
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Category pills                                                   */}
      {/* ---------------------------------------------------------------- */}
      <nav
        className="mb-6 -mx-4 px-4 overflow-x-auto flex gap-2 pb-2 scrollbar-hide"
        aria-label="Filter by category"
      >
        {availableCategories.map((category) => {
          const isActive = activeCategory === category;
          return (
            <button
              key={category}
              type="button"
              onClick={() => handleCategoryClick(category)}
              className={`flex-shrink-0 px-4 py-2 rounded-xl text-body-sm font-semibold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/30 ${
                isActive
                  ? 'bg-primary-500 text-white shadow-colored'
                  : 'bg-white text-surface-600 border border-surface-200 hover:bg-surface-100'
              }`}
              aria-pressed={isActive}
            >
              {category}
            </button>
          );
        })}
      </nav>

      {/* ---------------------------------------------------------------- */}
      {/* Main content                                                     */}
      {/* ---------------------------------------------------------------- */}
      {loading && <EventGridSkeleton count={6} />}

      {!loading && error && (
        <div className="max-w-md mx-auto mt-8">
          <div className="bg-white rounded-2xl shadow-soft-sm border border-error-400/20 p-6 text-center">
            <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-error-50 mb-4">
              <svg
                className="w-6 h-6 text-error-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-h3 text-error-600 mb-2">Error loading events</h3>
            <p className="text-body-sm text-error-500 mb-5">{error}</p>
            <button
              onClick={handleRetry}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-error-500 text-white text-body-sm font-semibold rounded-xl hover:bg-error-600 focus:outline-none focus:ring-2 focus:ring-error-500/40 focus:ring-offset-2 transition-colors duration-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      )}

      {!loading && !error && searchFilteredEvents.length === 0 && (
        <EmptyState
          icon={
            <svg
              className="w-7 h-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          }
          title="No events found"
          description={
            activeCategory !== 'All' || searchQuery
              ? 'Try changing your filters or search query to find more events.'
              : 'There are no events at this time. Be the first to create one!'
          }
          actionLabel="Create Event"
          actionTo="/create-cliqq"
        />
      )}

      {!loading && !error && searchFilteredEvents.length > 0 && (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {searchFilteredEvents.map((event) => {
            const id = event.ID || event.id;
            return (
              <motion.div key={id} variants={itemVariants}>
                <EventCard event={event} />
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Retry indicator */}
      {retryCount > 0 && (
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-surface-200 rounded-xl px-4 py-2 shadow-soft-xs">
            <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary-500 border-r-transparent" />
            <span className="text-body-sm text-surface-600">
              Retrying... ({retryCount}/{MAX_RETRY_ATTEMPTS})
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
